import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Factory,
  Briefcase,
  GraduationCap,
  Hospital,
  Globe2,
} from "lucide-react";
import Navbar from "../../components/Navbar";

const clientele = [
  {
    title: "Corporate Organizations",
    icon: Building2,
    desc: "Multinational and local enterprises seeking international standards and compliance excellence.",
  },
  {
    title: "Manufacturing Industry",
    icon: Factory,
    desc: "Factories and production units improving quality, safety, and operational efficiency.",
  },
  {
    title: "Professional Services",
    icon: Briefcase,
    desc: "Consulting firms and service providers enhancing credibility and client trust.",
  },
  {
    title: "Educational Institutions",
    icon: GraduationCap,
    desc: "Universities and training institutes adopting globally recognized frameworks.",
  },
  {
    title: "Healthcare Sector",
    icon: Hospital,
    desc: "Hospitals and medical organizations ensuring patient safety and quality standards.",
  },
  {
    title: "Global Clients",
    icon: Globe2,
    desc: "International clients across diverse regions and regulatory environments.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: "easeOut",
    },
  }),
};

const DiversifiedClientele = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-16 sm:py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===== Heading ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Our Diversified Clientele
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4" />
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            We proudly serve a wide spectrum of industries, helping organizations
            achieve excellence through globally recognized standards.
          </p>
        </motion.div>

        {/* ===== Cards Grid ===== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {clientele.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{
                  y: -12,
                  scale: 1.04,
                  rotateX: 6,
                  rotateY: -6,
                  boxShadow: "0px 30px 60px rgba(59,130,246,0.25)",
                }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="relative bg-white p-8 rounded-2xl shadow-lg border border-gray-100 overflow-hidden group"
              >
                {/* Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Icon */}
                <div className="relative z-10 w-16 h-16 flex items-center justify-center rounded-xl bg-[var(--bg-primary)] text-white mb-6 shadow-md">
                  <Icon className="w-8 h-8" />
                </div>

                {/* Title */}
                <h4 className="relative z-10 text-xl font-bold text-gray-900 mb-3 transition-colors group-hover:text-[var(--bg-primary)]">
                  {item.title}
                </h4>

                {/* Description */}
                <p className="relative z-10 text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
    </>
  );
};

export default DiversifiedClientele;
